/**
 * Blob-only analyst values on REOPEN (owner decision D2, 2026-09-26).
 *
 * G6 reopens by re-merging the template from the tables (owner rule 2026-09-09); OG reopens its
 * saved blob. A few values the analyst puts on the sheet are stored in NO table — OG keeps them only
 * inside that blob — so the re-merge cannot bring them back:
 *   • 1014 Anions E8 "Sample Filtered": a template default "No" with a Yes/No list (135 cells "Yes");
 *   • 1027 pH B6 "Temp Corr. Factor": a numeric template default (0.2) the analyst overwrites;
 *   • 8086 VOC Summary!H7 "IS Avg Resp." = FIELD("USERDEFINED5"), filled by the import and never
 *     saved (0 of 854 SampleInfo rows) — every per-run J4 reads it, so H / AQ went #DIV/0!;
 *   • 4014 Ammonia D5 = FIELD("READING3") (header, no stored value);
 *   • values in band columns the template leaves empty (8074 AV, 8073 T);
 *   • the whole "Calibration" sheet OG writes itself (the merge never fills it): the measured curve,
 *     Calibration ID, CalibratedDate / By (Phosphate 8076, Ortho Phosphate 8101) — see
 *     overlayMergeExcludedSheet.
 * The owner's decision: bring those back from the batch's SAVED sheet, and only those. Everything
 * the tables can supply still comes from the tables.
 *
 * A cell is taken from the saved sheet ONLY when all of these hold:
 *   – the saved sheet holds a typed VALUE there (not a formula, not blank, not an error);
 *   – the template cell is one the analyst fills, and G6 still shows the template's own state:
 *       HEADER (above the band) — a pure FIELD("X") the merge left EMPTY, or a constant that is a
 *         NUMBER or carries a data-validation LIST, still equal to that constant;
 *       BAND rows (matched by SysSampleCode | Parameter | RunNo, never by position) — an EMPTY
 *         template cell the merge left empty, or a NUMBER / LIST constant still equal to it;
 *   – drift guard: the caption beside it is the same in the template and the saved sheet (header:
 *     the nearest text cell to its left; band: the column caption in the row above the band), so an
 *     older template layout never pours values into the wrong cells.
 * Formulas, band FIELD cell VALUES (tables own them — e.g. SpikeAmount stays blank by design), captions,
 * Raw Data / RawDataSheet and calibration sheets are never touched.
 *
 * ECHO (2026-09-30): a header FIELD of a Continuous field DataParsing places outside the band (VOC Remark at B6)
 * is NOT taken when the saved value equals what that same column holds on the saved sheet's band rows. OG's
 * save stores such a field per band row from its column (the run's QC type), and the earlier re-merge echoed
 * that stored value into the header cell, so AI-saved sheets carry "CCV" / "Sample" under Remarks. A remark the
 * analyst really typed there differs from the column and still comes back.
 *
 * TYPE (2026-09-26): a pure FIELD cell the tables DID fill takes the saved cell's type when the saved
 * cell holds the same value typed the other way (number vs numeric text, TRUE vs "True") — the value
 * never changes, only how OG stored it (see sameValueOtherType). Same row matching and drift guards as
 * above. Where there is no saved sheet (or no matching row) the merge's own typing (option B) stands.
 */

import { loadSdmsWorkbook, writeSdmsWorkbook } from './sdmsWorkbookIo';

type Ws = import('exceljs').Worksheet;
type Cell = import('exceljs').Cell;

const FIELD_ONLY_RE = /^\s*=?\s*FIELD\(\s*"([^"]+)"\s*\)\s*$/i;
const FIELD_ANY_RE = /FIELD\(\s*"([^"]+)"\s*\)/gi;
/** Sheets the re-merge fills from elsewhere (raw-data carry, calibration-set re-embed). */
export const SKIP_SHEET_RE = /^(raw ?data|rawdatasheet|level\d+)$/i;
/** A sheet the merge NEVER fills — OG excludes "Calibration" from the FIELD merge and its own routines
 *  write it (the measured curve from the import's CalculatedConc, CalibrationID / CalibratedDate / By from
 *  the linked calibration: CalibAndABIDSupport :14836, Dc_AcceptingImport :11990). Every typed value OG
 *  saved in a cell the template leaves empty is therefore blob-only. */
export const MERGE_EXCLUDED_SHEET_RE = /^calibration$/i;

function formulaOf(cell: Cell): string | null {
  const v = cell.value as { formula?: string; sharedFormula?: string } | null;
  if (v && typeof v === 'object' && ('formula' in v || 'sharedFormula' in v)) return v.formula ?? v.sharedFormula ?? '';
  return null;
}

/** A plain typed value (no formula / error / blank), normalised for writing back. */
function typedValue(cell: Cell): unknown | undefined {
  const v = cell.value as unknown;
  if (v === null || v === undefined || v === '') return undefined;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? undefined : v;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('formula' in o || 'sharedFormula' in o || 'error' in o) return undefined;
    if ('richText' in o) {
      const t = (o.richText as Array<{ text: string }>).map(r => r.text).join('');
      return t.trim() === '' ? undefined : t;
    }
    return undefined;
  }
  if (typeof v === 'string' && v.trim() === '') return undefined;
  return v;
}

const NUMERIC_TEXT_RE = /^\s*[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?\s*$/;

/** The saved cell's value when it is the SAME value as the merged cell's, only typed the other way
 *  (number vs numeric text, or TRUE / FALSE vs "True" / "False") — else undefined. OG's saved sheet
 *  keeps each cell's own type: a value the
 *  analyst typed or pasted is a number, one the merge / import wrote is text ("0.001"), and the same
 *  field differs from cell to cell (NumericResult: 526 cells number, 1135 text over the harness batches),
 *  so no per-field rule can reproduce it. The type matters beyond looks: `J11<VALUE(AV11)` is FALSE for
 *  the text "0.001" and TRUE for the number (Metals 8060 "Adjusted S2" came back 0.05 instead of 0). */
function sameValueOtherType(merged: unknown, saved: unknown): unknown | undefined {
  const num = (v: unknown): number | undefined =>
    typeof v === 'number' ? v : (typeof v === 'string' && NUMERIC_TEXT_RE.test(v) ? Number(v) : undefined);
  if (typeof merged === typeof saved) return undefined;
  // A boolean OG stored as a real TRUE / FALSE where the merge wrote the text "True" / "False" (IsReport).
  if (typeof saved === 'boolean' && typeof merged === 'string')
    return /^(true|false)$/i.test(merged.trim()) && (merged.trim().toLowerCase() === 'true') === saved ? saved : undefined;
  const a = num(merged), b = num(saved);
  if (a === undefined || b === undefined || !Number.isFinite(a) || !Number.isFinite(b)) return undefined;
  return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a)) ? saved : undefined;
}

/** A cell the re-merged sheet does not have, as a dry run sees it: exactly what ExcelJS's getCell would
 *  create (no value). Shared and never written. */
const BLANK = { value: null } as unknown as Cell;

/** The re-merged sheet's cell (r, c). The real pass uses getRow / getCell, which CREATE a missing row /
 *  cell (the original behaviour, written out with the values); the dry pass only looks. */
function curCell(ws: Ws, r: number, c: number, write: boolean): Cell {
  return write ? ws.getRow(r).getCell(c) : (ws.findRow(r)?.findCell(c) ?? BLANK);
}

const isEmpty = (cell: Cell): boolean => {
  const v = cell.value as unknown;
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
};
const sameValue = (a: unknown, b: unknown): boolean =>
  a instanceof Date && b instanceof Date ? a.getTime() === b.getTime() : String(a ?? '').trim() === String(b ?? '').trim();

function hasListValidation(ws: Ws, address: string): boolean {
  // ExcelJS has the collection at runtime but its typings don't declare it.
  const dv = (ws as unknown as { dataValidations?: { find?: (a: string) => { type?: string } | undefined; model?: Record<string, { type?: string }> } }).dataValidations;
  const rule = dv?.find?.(address) ?? dv?.model?.[address];
  return rule?.type === 'list';
}

/** A template constant the analyst overwrites: a number, or a cell with a pick list. */
function isEntryConstant(ws: Ws, cell: Cell): boolean {
  const v = cell.value as unknown;
  if (v === null || v === undefined || typeof v === 'object') return false;
  return typeof v === 'number' || hasListValidation(ws, cell.address);
}

export interface Band { row: number; sysCol: number; parCol: number; runCol?: number; lastCol: number }

/** The detail band: the template row holding FIELD("PARAMETER") and ≥ 3 FIELD tokens. */
export function findBand(ws: Ws): Band | null {
  let band: Band | null = null;
  ws.eachRow({ includeEmpty: false }, (row, r) => {
    if (band) return;
    const fields = new Map<string, number>();
    let lastCol = 0;
    row.eachCell({ includeEmpty: false }, (cell, c) => {
      lastCol = Math.max(lastCol, c);
      const f = formulaOf(cell);
      if (f) for (const m of f.matchAll(FIELD_ANY_RE)) if (!fields.has(m[1]!.toUpperCase())) fields.set(m[1]!.toUpperCase(), c);
    });
    const par = fields.get('PARAMETER');
    const sys = fields.get('SYSSAMPLECODE');
    if (par && sys && fields.size >= 3) band = { row: r, sysCol: sys, parCol: par, runCol: fields.get('RUNNO'), lastCol };
  });
  return band;
}

export const text = (cell: Cell): string => {
  const v = typedValue(cell);
  return v === undefined ? '' : (v instanceof Date ? v.toISOString() : String(v)).trim();
};

/** Band rows of a merged / saved sheet, keyed SysSampleCode|Parameter|RunNo (first occurrence wins). */
export function bandRows(ws: Ws, band: Band, write = true): Map<string, number> {
  const out = new Map<string, number>();
  const last = ws.actualRowCount > 0 ? ws.rowCount : 0;
  for (let r = band.row; r <= last; r++) {
    const sys = text(curCell(ws, r, band.sysCol, write)).toUpperCase();
    const par = text(curCell(ws, r, band.parCol, write)).toUpperCase();
    if (!sys || !par) continue;
    const run = band.runCol ? (text(curCell(ws, r, band.runCol, write)) || '1') : '1';
    const key = `${sys}|${par}|${run}`;
    if (!out.has(key)) out.set(key, r);
  }
  return out;
}

/** A caption as the drift guards compare it: line breaks and spacing normalised. OG stores a line break inside a
 *  caption as "_x000d_" + LF (CRLF), a G6-saved sheet as LF only, so "Rec %" read as two captions and every guard
 *  failed on a G6-saved sheet (2026-10-01). */
export const normCaption = (s: string): string => s.replace(/_x000d_/gi, '').replace(/\s+/g, ' ').trim();

/** Nearest non-empty TEXT cell to the left of (r, c) — the header caption drift guard. */
function captionLeft(ws: Ws, r: number, c: number): string {
  for (let cc = c - 1; cc >= 1; cc--) {
    const v = ws.getRow(r).getCell(cc).value as unknown;
    if (typeof v === 'string' && v.trim()) return normCaption(v);
    if (v && typeof v === 'object' && 'richText' in (v as object)) return normCaption(text(ws.getRow(r).getCell(cc)));
  }
  return '';
}

/** A merge-excluded sheet (Calibration): every typed value in a cell the TEMPLATE leaves empty and the
 *  re-merge left empty comes from the saved sheet. Layout guard: every TEXT caption in the template's first
 *  two rows must read the same in the saved sheet, else nothing is taken. */
function overlayMergeExcludedSheet(tpl: Ws, cur: Ws, saved: Ws, write = true): number {
  for (let r = 1; r <= 2; r++) {
    let drift = false;
    tpl.getRow(r).eachCell({ includeEmpty: false }, (tc, c) => {
      if (typeof tc.value === 'string' && tc.value.trim() && normCaption(text(saved.getRow(r).getCell(c))) !== normCaption(tc.value)) drift = true;
    });
    if (drift) return 0;
  }
  let n = 0;
  saved.eachRow({ includeEmpty: false }, (row, r) => {
    row.eachCell({ includeEmpty: false }, (sc, c) => {
      const sv = typedValue(sc);
      if (sv === undefined) return;
      const tc = tpl.getRow(r).getCell(c);
      if (!isEmpty(tc) || formulaOf(tc) !== null) return;   // the template's own content stays
      const cc = curCell(cur, r, c, write);
      if (!isEmpty(cc) || formulaOf(cc) !== null) return;
      if (write) cc.value = sv as never;
      n++;
    });
  });
  return n;
}

/** Overlay one sheet; returns the number of cells taken from the saved sheet. */
function overlaySheet(tpl: Ws, cur: Ws, saved: Ws, write = true, continuousHeader: ReadonlySet<string> = new Set()): number {
  let n = 0;
  const band = findBand(tpl);
  const headerLast = band ? band.row - 1 : tpl.rowCount;
  /** Does column `c` of the saved sheet's band rows hold `v`? (the band echo — see ECHO above) */
  const bandColumnHolds = (c: number, v: unknown): boolean => {
    if (!band) return false;
    let hit = false;
    saved.eachRow({ includeEmpty: false }, (row, r) => {
      if (hit || r < band.row) return;
      const bv = typedValue(row.getCell(c));
      if (bv !== undefined && sameValue(bv, v)) hit = true;
    });
    return hit;
  };
  // ── Header (above the band) ──
  for (let r = 1; r <= headerLast; r++) {
    tpl.getRow(r).eachCell({ includeEmpty: false }, (tc, c) => {
      const f = formulaOf(tc);
      const pureField = f !== null && FIELD_ONLY_RE.test(f);
      const constant = f === null && isEntryConstant(tpl, tc);
      if (!pureField && !constant) return;
      const cc = curCell(cur, r, c, write);
      if (pureField && !isEmpty(cc)) {
        // The tables supplied it: only its TYPE may follow the saved sheet.
        const t = formulaOf(cc) === null ? sameValueOtherType(cc.value, typedValue(saved.getRow(r).getCell(c))) : undefined;
        if (t === undefined) return;
        const cap = captionLeft(tpl, r, c);
        if (!cap || cap !== captionLeft(saved, r, c)) return;
        if (write) cc.value = t as never;
        n++;
        return;
      }
      if (!pureField && !sameValue(cc.value, tc.value)) return;
      const sv = typedValue(saved.getRow(r).getCell(c));
      if (sv === undefined || sameValue(sv, cc.value)) return;
      const cap = captionLeft(tpl, r, c);
      if (!cap || cap !== captionLeft(saved, r, c)) return;
      if (pureField && continuousHeader.has(FIELD_ONLY_RE.exec(f!)![1]!.trim().toUpperCase()) && bandColumnHolds(c, sv)) return;
      if (write) cc.value = sv as never;
      n++;
    });
  }
  // ── Band rows, matched by key ──
  if (band) {
    const capRow = band.row - 1;
    const cols: Array<{ c: number; constant: unknown }> = [];
    const fieldCols: number[] = [];                              // pure FIELD → the type may follow
    const tBand = tpl.getRow(band.row);
    for (let c = 1; c <= band.lastCol; c++) {
      const tc = tBand.getCell(c);
      const cap = capRow >= 1 ? normCaption(text(tpl.getRow(capRow).getCell(c))) : '';
      if (cap !== (capRow >= 1 ? normCaption(text(saved.getRow(capRow).getCell(c))) : '')) continue;   // drift guard
      const f = formulaOf(tc);
      if (f !== null) {                                         // FIELD / formula → tables / engine
        if (FIELD_ONLY_RE.test(f)) fieldCols.push(c);
        continue;
      }
      const empty = isEmpty(tc);
      if (!empty && !isEntryConstant(tpl, tc)) continue;        // a caption-like constant
      cols.push({ c, constant: empty ? undefined : tc.value });
    }
    if (cols.length || fieldCols.length) {
      const curRows = bandRows(cur, band, write);
      const savedRows = bandRows(saved, band);
      for (const [key, cr] of curRows) {
        const sr = savedRows.get(key);
        if (!sr) continue;
        for (const c of fieldCols) {
          const cc = curCell(cur, cr, c, write);
          if (formulaOf(cc) !== null) continue;
          const t = sameValueOtherType(cc.value, typedValue(saved.getRow(sr).getCell(c)));
          if (t === undefined) continue;
          if (write) cc.value = t as never;
          n++;
        }
        for (const { c, constant } of cols) {
          const cc = curCell(cur, cr, c, write);
          if (formulaOf(cc) !== null) continue;
          if (constant === undefined ? !isEmpty(cc) : !sameValue(cc.value, constant)) continue;
          const sv = typedValue(saved.getRow(sr).getCell(c));
          if (sv === undefined || sameValue(sv, cc.value)) continue;
          if (write) cc.value = sv as never;
          n++;
        }
      }
    }
  }
  return n;
}

/**
 * Put the blob-only analyst values of the batch's SAVED sheet (`savedBuf`) back onto the re-merged
 * workbook (`mergedBuf`), per the rules above. `templateBuf` is the batch's template. Returns the new
 * buffer and how many cells were taken; `mergedBuf` unchanged when none.
 */
export async function overlayBlobOnlyValues(
  mergedBuf: ArrayBuffer, templateBuf: ArrayBuffer, savedBuf: ArrayBuffer,
  /** Template Model: 1 = one sheet per run (named by its sample code), read against the template's
   *  first sheet. Any other model only overlays sheets the template itself has. */
  model = 0,
): Promise<{ buf: ArrayBuffer; count: number }> {
  try {
    const cur = await loadSdmsWorkbook(mergedBuf);
    const count = await overlayBlobOnlyValuesIntoWorkbook(cur, templateBuf, savedBuf, model);
    if (!count) return { buf: mergedBuf, count: 0 };
    return { buf: await writeSdmsWorkbook(cur), count };
  } catch (e) {
    console.warn('[SDMS] blob-only value overlay skipped', e);
    return { buf: mergedBuf, count: 0 };
  }
}

/** The same overlay onto a workbook already in memory (the open chain — sdmsWorkbookIo.ts). Returns
 *  how many cells were taken (0 = `cur` untouched). Throws when the template or saved sheet cannot be
 *  read — before `cur` is touched, so the caller can skip the step and keep the merge as it is. */
export async function overlayBlobOnlyValuesIntoWorkbook(
  cur: import('exceljs').Workbook, templateBuf: ArrayBuffer, savedBuf: ArrayBuffer, model = 0,
  /** Merge-data continuousHeaderFields (VOC Remark) — their header cells never take the band echo (see ECHO). */
  continuousHeaderFields: readonly string[] | null = null,
  /** The template and saved sheet already read (the open chain reads them once for this and the rounding carry). */
  parsed?: { tpl: import('exceljs').Workbook; saved: import('exceljs').Workbook },
): Promise<number> {
  const [tpl, saved] = parsed ? [parsed.tpl, parsed.saved]
    : await Promise.all([loadSdmsWorkbook(templateBuf), loadSdmsWorkbook(savedBuf)]);
  const continuousHeader = new Set((continuousHeaderFields ?? []).map(k => k.trim().toUpperCase()));
  const tplByName = new Map(tpl.worksheets.map(w => [w.name.toLowerCase(), w]));
  const main = tpl.worksheets[0];
  const run = (write: boolean): number => {
    let count = 0;
    for (const ws of cur.worksheets) {
      if (SKIP_SHEET_RE.test(ws.name.trim())) continue;
      const sv = saved.worksheets.find(w => w.name.toLowerCase() === ws.name.toLowerCase());
      const tw = tplByName.get(ws.name.toLowerCase()) ?? (model === 1 ? main : undefined);
      if (!sv || !tw) continue;
      count += MERGE_EXCLUDED_SHEET_RE.test(ws.name.trim()) && tplByName.has(ws.name.toLowerCase())
        ? overlayMergeExcludedSheet(tw, ws, sv, write)
        : overlaySheet(tw, ws, sv, write, continuousHeader);
    }
    return count;
  };
  // Count first without touching `cur`, then — only when something is taken — the real pass. Its
  // reads create the rows / cells they look at (ExcelJS getRow / getCell), and the buffer form dropped
  // those whenever nothing was taken (it returned the untouched bytes); on the shared in-memory
  // workbook they would be written out.
  return run(false) ? run(true) : 0;
}
